import React, { useState, useEffect } from 'react';
import { HardDrive, Users, User, BarChart3, Shield, ArrowLeft, RefreshCw, Upload, FolderOpen, FileText, Database, Activity } from 'lucide-react';
import { useDriveFiles } from '../../hooks/useDriveFiles';
import { FileUpload } from './FileUpload';
import { FileList } from './FileList';
import { useAuthStore } from '../../stores/authStore';
import { formatFileSize, MAX_TOTAL_SIZE } from '../../types/drive';
import { userService } from '../../services/userService';

export const DrivePage: React.FC = () => {
  const { user, userProfile } = useAuthStore();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [showUpload, setShowUpload] = useState(false);
  const [users, setUsers] = useState<{ id: string; name: string; email: string; role?: string }[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    files,
    stats,
    loading,
    error,
    canAccessAllFiles,
    isAdmin,
    isModerator,
  } = useDriveFiles({ targetUserId: selectedUserId });

  const currentUserId = selectedUserId || user?.uid;
  const isViewingOwnFiles = !selectedUserId || selectedUserId === user?.uid;

  // Calcola percentuale spazio utilizzato
  const usedSpace = stats?.totalSize || 0;
  const usedPercentage = (usedSpace / MAX_TOTAL_SIZE) * 100;

  // Carica lista utenti per admin/moderatori
  useEffect(() => {
    if (canAccessAllFiles) {
      setLoadingUsers(true);
      userService.getAllUsers()
        .then(setUsers)
        .catch(console.error)
        .finally(() => setLoadingUsers(false));
    }
  }, [canAccessAllFiles]);

  // Ottieni nome utente corrente
  const getCurrentUserName = () => {
    if (!selectedUserId) return 'Tutti gli utenti';
    if (selectedUserId === user?.uid) return 'I miei file';
    const selectedUser = users.find(u => u.id === selectedUserId);
    return selectedUser ? `${selectedUser.name} (${selectedUser.email})` : 'Utente sconosciuto';
  };

  // Gestione completamento upload
  const handleUploadComplete = () => {
    // L'hook si aggiorna automaticamente grazie ai listener in tempo reale
  };

  // Gestione eliminazione file
  const handleFileDelete = () => {
    // L'hook si aggiorna automaticamente grazie ai listener in tempo reale
  };

  // Gestione refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Ricarica i dati
      window.location.reload();
    } catch (error) {
      console.error('Errore durante il refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Statistiche per le card
  const driveStats = {
    totalFiles: files?.length || 0,
    totalSize: usedSpace,
    usedPercentage: usedPercentage.toFixed(1),
    userCount: canAccessAllFiles ? users.length : 1
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <HardDrive className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Devi essere autenticato per accedere al Drive
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header Desktop */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.history.back()}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                  <HardDrive className="h-6 w-6 text-blue-700" />
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                    Drive
                  </h1>
                  <p className="text-sm text-gray-600">
                    {canAccessAllFiles 
                      ? `Gestione file ${getCurrentUserName()}`
                      : 'Gestisci i tuoi file personali'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 lg:gap-4">
              {/* Indicatore permessi */}
              {canAccessAllFiles && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-lg border border-blue-200">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {isAdmin ? 'Amministratore' : 'Moderatore'}
                  </span>
                </div>
              )}

              {/* Selettore utente per admin/moderatori */}
              {canAccessAllFiles && (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(e.target.value || undefined)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={loadingUsers}
                  >
                    <option value="">Tutti gli utenti</option>
                    <option value={user?.uid}>I miei file</option>
                    {users.map(userItem => (
                      <option key={userItem.id} value={userItem.id}>
                        {userItem.name} ({userItem.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Aggiorna"
              >
                <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => setShowUpload(!showUpload)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Upload className="h-4 w-4" />
                <span className="hidden lg:inline">
                  {showUpload ? 'Nascondi Upload' : 'Carica File'}
                </span>
                <span className="lg:hidden">
                  {showUpload ? 'Nascondi' : 'Upload'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Header Mobile Ottimizzato */}
        <div className="lg:hidden bg-white rounded-2xl shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.history.back()}
                className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg">
                  <HardDrive className="h-4 w-4 text-blue-700" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Drive</h1>
                  <p className="text-xs text-gray-600">
                    {canAccessAllFiles 
                      ? `Gestione file ${getCurrentUserName()}`
                      : 'Gestisci i tuoi file personali'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Aggiorna"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => setShowUpload(!showUpload)}
                className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm"
              >
                <Upload className="h-3.5 w-3.5" />
                <span>{showUpload ? 'Nascondi' : 'Upload'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Statistiche Cards */}
        <div className={`grid gap-1.5 lg:gap-4 ${showUpload ? 'hidden lg:grid lg:grid-cols-4' : 'grid-cols-4 lg:grid-cols-4'}`}>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg lg:rounded-2xl p-1.5 lg:p-4 border border-blue-200">
            <div className="flex items-center justify-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-2">
              <FileText className="h-3 w-3 lg:h-5 lg:w-5 text-blue-600" />
              <span className="text-sm lg:text-2xl font-bold text-blue-700">
                {driveStats.totalFiles}
              </span>
            </div>
            <div className="text-center">
              <div className="text-[10px] lg:text-sm text-blue-600 font-medium">
                File Totali
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg lg:rounded-2xl p-1.5 lg:p-4 border border-green-200">
            <div className="flex items-center justify-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-2">
              <Database className="h-3 w-3 lg:h-5 lg:w-5 text-green-600" />
              <span className="text-xs lg:text-lg font-bold text-green-700">
                {formatFileSize(driveStats.totalSize)}
              </span>
            </div>
            <div className="text-center">
              <div className="text-[10px] lg:text-sm text-green-600 font-medium">
                Spazio Usato
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg lg:rounded-2xl p-1.5 lg:p-4 border border-yellow-200">
            <div className="flex items-center justify-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-2">
              <Activity className="h-3 w-3 lg:h-5 lg:w-5 text-yellow-600" />
              <span className="text-xs lg:text-lg font-bold text-yellow-700">
                {driveStats.usedPercentage}%
              </span>
            </div>
            <div className="text-center">
              <div className="text-[10px] lg:text-sm text-yellow-600 font-medium">
                Utilizzato
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg lg:rounded-2xl p-1.5 lg:p-4 border border-purple-200">
            <div className="flex items-center justify-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-2">
              <Users className="h-3 w-3 lg:h-5 lg:w-5 text-purple-600" />
              <span className="text-sm lg:text-2xl font-bold text-purple-700">
                {driveStats.userCount}
              </span>
            </div>
            <div className="text-center">
              <div className="text-[10px] lg:text-sm text-purple-600 font-medium">
                Utenti
              </div>
            </div>
          </div>
        </div>

        {/* Barra di progresso spazio */}
        <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6 ${showUpload ? 'hidden lg:block' : ''}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-gray-600" />
              <span className="text-sm font-semibold text-gray-900">
                Spazio utilizzato
              </span>
            </div>
            <span className="text-sm text-gray-600">
              {formatFileSize(usedSpace)} / {formatFileSize(MAX_TOTAL_SIZE)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 shadow-sm ${
                usedPercentage > 90 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                usedPercentage > 75 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 
                'bg-gradient-to-r from-blue-500 to-blue-600'
              }`}
              style={{ width: `${Math.min(usedPercentage, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{stats?.fileCount || 0} file</span>
            <span>{usedPercentage.toFixed(1)}% utilizzato</span>
          </div>
        </div>

        {/* Contenuto principale */}
        <div className="space-y-4 lg:space-y-6">
          {/* Area upload */}
          {showUpload && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-br from-green-100 to-green-200 rounded-xl">
                  <Upload className="h-5 w-5 text-green-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Carica nuovi file
                  </h3>
                  {!isViewingOwnFiles && (
                    <p className="text-sm text-blue-600">
                      per {getCurrentUserName()}
                    </p>
                  )}
                </div>
              </div>
              <FileUpload 
                onUploadComplete={handleUploadComplete} 
                targetUserId={selectedUserId}
              />
            </div>
          )}

          {/* Lista file */}
          <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6 ${showUpload ? 'hidden lg:block' : ''}`}>
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl">
                  <FolderOpen className="h-5 w-5 text-purple-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isViewingOwnFiles ? 'I tuoi file' : `File di ${getCurrentUserName()}`}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isViewingOwnFiles 
                      ? 'Gestisci i tuoi file personali'
                      : 'Visualizzazione file di altri utenti'
                    }
                  </p>
                </div>
              </div>
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg border border-red-200">
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}
            </div>

            <FileList
              files={files}
              loading={loading}
              onFileDelete={handleFileDelete}
            />
          </div>
        </div>
      </div>
    </div>
  );
}; 